import { useState, useEffect, useCallback, useRef } from "react";
import { useGameEngine } from "../hooks/useGameEngine";
import { ShareButton } from "../components/ShareButton";
import VinylShareCard from "../components/VinylShareCard";
import { isNativePlatform } from "../lib/platform";
import { Confetti } from "../components/Confetti";
import {
  getTodayKey,
  fetchDailyChallenge,
  getFallbackSong,
  type DailyChallenge as DailyChallengeData,
} from "../lib/dailySongs";
import { getStats } from "../lib/storage";
import { saveDailyScore, hasPlayedToday, getPlayerStats } from "../lib/scores";
import { recordPlayedSong } from "../lib/playedSongs";
import { incrementGamesPlayed } from "../hooks/useInstallPrompt";
import type { Stage } from "../audio";
import type { ShareData } from "../lib/shareCard";
import { useAuth } from "../hooks/useAuth";
import { trackEvent } from "../lib/clarityEvents";
import { useToast } from "../components/Toast";
import { useLocale } from "../lib/i18n";
import { T, Mark, ScoreChip, WaveForm, BigPlayButton, ActionChip, LetterBank, letterBankMatches } from "../components/v2";

type GamePhase =
  | "loading"
  | "start"
  | "playing"
  | "correct"
  | "failed"
  | "already-played";

const engineOptions = { revealMode: "notes" as const };
const TOTAL_STAGES = 6;

export function DailyChallenge({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const auth = useAuth();
  const toast = useToast();
  const { t, locale } = useLocale();
  const he = locale === "he";

  const engine = useGameEngine({
    ...engineOptions,
    onTrackError: (msg) =>
      toast.show(msg.includes("not found") ? t("daily.songUnavailable") : msg, "error"),
  });

  const [phase, setPhase] = useState<GamePhase>("loading");
  const [guesses, setGuesses] = useState<string[]>([]);
  const [wrongSignal, setWrongSignal] = useState(0);
  const [stats, setStats] = useState(getStats());
  const [challenge, setChallenge] = useState<DailyChallengeData | null>(null);
  const [existingResult, setExistingResult] = useState<{ stage: number; score: number } | null>(null);
  const [artistRevealed, setArtistRevealed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  // Scoring (page-level, ignores engine.score):
  // base = 90 at note 1 with no hints. Each subsequent note bought is -10,
  // and the artist hint is -30 (one-shot). So:
  //   note 1, no hint  → 90; with hint → 60
  //   note 2, no hint  → 80; with hint → 50
  //   ...
  //   note 6, no hint  → 40; with hint → 10
  const BASE_SCORE = 90;
  const PER_NOTE_PENALTY = 10;
  const ARTIST_HINT_COST = 30;

  // Dev-only fixture: ?devPhase=correct|failed renders the Victory layout
  // with deterministic mock data. Removed in production builds.
  const devPhaseParam = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get("devPhase")
    : null;

  const [justFinished, setJustFinished] = useState(false);

  const todayKey = getTodayKey();
  const dailyNumber = challenge?.challengeNumber ?? 1;
  const dailySong = challenge?.song ?? getFallbackSong(todayKey);

  useEffect(() => {
    Promise.all([fetchDailyChallenge(todayKey), hasPlayedToday(todayKey)]).then(
      ([c, existing]) => {
        if (c) setChallenge(c);
        if (existing) {
          setPhase("already-played");
          setGuesses(existing.guesses);
          setExistingResult({ stage: existing.stage, score: existing.score });
        } else {
          // No separate "start" screen — drop straight onto the game stage.
          // The big play button there acts as the first CTA: while
          // engine.track is null + not loading, tapping it triggers
          // handleStart (loads the daily song and begins playback).
          setPhase("playing");
        }
        getPlayerStats().then(setStats);

        if (c && "serviceWorker" in navigator && navigator.serviceWorker.controller) {
          const dateKey = getTodayKey();
          const base =
            import.meta.env.DEV || isNativePlatform()
              ? "https://itunes.apple.com/search"
              : "/api/itunes";
          const searchUrl = `${base}?term=${encodeURIComponent(
            c.song.artist + " " + c.song.title
          )}&media=music&entity=song&limit=5`;
          fetch(searchUrl)
            .then((res) => res.json())
            .then((data) => {
              const result = data.results?.find(
                (r: { previewUrl?: string; artistName?: string; trackName?: string }) =>
                  r.previewUrl && r.artistName && r.trackName
              );
              if (result?.previewUrl) {
                navigator.serviceWorker.controller!.postMessage({
                  type: "CACHE_DAILY_AUDIO",
                  url: result.previewUrl,
                  dateKey,
                });
              }
            })
            .catch(() => {});
        }
      }
    );
  }, [todayKey]);

  const engineRef = useRef(engine);
  engineRef.current = engine;

  useEffect(() => {
    if (phase === "correct" || phase === "failed") {
      engineRef.current.pause();
    }
  }, [phase]);

  const guessesRef = useRef(guesses);
  guessesRef.current = guesses;

  const handleStart = useCallback(async () => {
    setGuesses([]);
    setArtistRevealed(false);
    setHintPenalty(0);
    setPhase("playing");
    trackEvent("game_start");
    await engineRef.current.loadAndPlay(
      dailySong.artist,
      dailySong.title,
      { kind: "random" },
      dailySong.itunesId
    );
  }, [dailySong.artist, dailySong.title, dailySong.itunesId]);

  const finishGame = useCallback(
    (stg: Stage | 0, scr: number, finalGuesses: string[]) => {
      trackEvent("game_complete");
      const result = { dateKey: todayKey, stage: stg, score: scr, guesses: finalGuesses };
      incrementGamesPlayed();
      if (window.matchMedia("(display-mode: standalone)").matches) {
        const key = "nigunim:gamesAfterInstall";
        const current = parseInt(localStorage.getItem(key) || "0", 10);
        localStorage.setItem(key, String(current + 1));
      }
      saveDailyScore(result).then(() => {
        getPlayerStats().then(setStats);
      });
      if (challenge?.challengeId) {
        recordPlayedSong(challenge.challengeId, "daily", engine.track?.startSec ?? 0);
      }
    },
    [todayKey, challenge, engine.track]
  );

  const handleGuess = useCallback(
    async (title: string) => {
      const eng = engineRef.current;
      const g = guessesRef.current;
      if (!eng.track) return;
      // Title-only matching against any of the bilingual title forms.
      const candidates = [
        eng.track.track.title,
        dailySong.titleHe,
        dailySong.titleEn,
      ].filter((s): s is string => !!s);
      const correct = candidates.some((c) => letterBankMatches(c, title));

      if (correct) {
        const newGuesses = [...g, "correct"];
        setGuesses(newGuesses);
        setPhase("correct");
        setJustFinished(true);
        // Score = base − notes-bought × 10 − (artist hint ? 30 : 0).
        const notesBought = eng.stage - 1;
        const finalScore = Math.max(
          0,
          BASE_SCORE - notesBought * PER_NOTE_PENALTY - hintPenalty
        );
        finishGame(eng.stage, finalScore, newGuesses);
        await eng.reveal();
      } else {
        // Wrong placement does NOT advance the audio stage — only the
        // explicit "more note" action chip costs notes. Player can retry
        // the bank as many times as they want.
        setGuesses([...g, "wrong"]);
        setWrongSignal((s) => s + 1);
      }
    },
    [finishGame, dailySong.titleHe, dailySong.titleEn, hintPenalty]
  );

  const handleArtistHint = useCallback(() => {
    if (artistRevealed) return;
    setArtistRevealed(true);
    setHintPenalty((p) => p + ARTIST_HINT_COST);
  }, [artistRevealed]);

  const handleRevealHint = useCallback(async () => {
    const eng = engineRef.current;
    const g = guessesRef.current;
    const newGuesses = [...g, "skip"];
    setGuesses(newGuesses);
    const hasNext = await eng.skip();
    if (!hasNext) {
      setPhase("failed");
      setJustFinished(true);
      finishGame(0, 0, newGuesses);
      await eng.reveal();
    }
  }, [finishGame]);

  const isPlaying = engine.playback.status === "playing";

  const shareData: ShareData | null =
    phase === "correct" || phase === "failed" || phase === "already-played"
      ? {
          dailyNumber,
          stage: (phase === "failed"
            ? 0
            : phase === "already-played"
            ? existingResult?.stage ?? 0
            : engine.stage || 0) as Stage | 0,
          score:
            phase === "failed"
              ? 0
              : phase === "already-played"
              ? existingResult?.score ?? 0
              : engine.score,
          guesses,
          maxStages: TOTAL_STAGES,
        }
      : null;

  // ── Loading ──
  if (phase === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-20" dir={he ? "rtl" : "ltr"}>
        <div
          className="w-12 h-12 rounded-full"
          style={{
            border: `3px solid ${T.hairline}`,
            borderTopColor: T.gold,
            animation: "nig-rotate-slow 0.8s linear infinite",
          }}
        />
        <p
          className="mt-4"
          style={{
            fontFamily: T.sans,
            fontSize: 11,
            letterSpacing: "0.18em",
            color: T.mist,
          }}
        >
          {t("common.loading")}
        </p>
      </div>
    );
  }

  if (devPhaseParam === "correct" || devPhaseParam === "failed") {
    const won = devPhaseParam === "correct";
    return (
      <VictoryScreen
        won={won}
        stage={won ? 3 : 6}
        score={won ? 480 : 0}
        guesses={won ? ["wrong", "skip", "correct"] : ["wrong", "skip", "skip", "skip", "skip", "wrong"]}
        currentStreak={won ? 13 : 0}
        title={won ? "אדון עולם" : "ניגון של היום"}
        artist={won ? "ר׳ שלמה קרליבך · 1972" : "—"}
        artworkUrl={undefined}
        shareData={{
          dailyNumber: 6,
          stage: (won ? 3 : 0) as Stage | 0,
          score: won ? 480 : 0,
          guesses: [],
          maxStages: TOTAL_STAGES,
        }}
        showShareCard={won}
        engineGenre="Chassidic"
        displayName={undefined}
        onBack={() => onNavigate?.("home")}
        he={he}
        t={t}
      />
    );
  }

  // No "start" branch: phase goes loading → playing directly. The big
  // play button on the game stage acts as the first CTA when the engine
  // hasn't loaded yet (see onTogglePlay handler below).

  // ── Already played ──
  if (phase === "already-played") {
    return (
      <AlreadyPlayedScreen
        existingResult={existingResult}
        guesses={guesses}
        stats={stats}
        shareData={shareData}
        justFinished={justFinished}
        engineGenre={engine.track?.track.genre}
        displayName={auth.displayName ?? undefined}
        onBack={() => onNavigate?.("home")}
        he={he}
        t={t}
      />
    );
  }

  // ── Correct / Failed → Victory layout ──
  if (phase === "correct" || phase === "failed") {
    return (
      <VictoryScreen
        won={phase === "correct"}
        stage={engine.stage}
        score={engine.score}
        guesses={guesses}
        currentStreak={stats.currentStreak}
        title={
          he
            ? dailySong.titleHe ?? engine.track?.track.title ?? ""
            : dailySong.titleEn ?? engine.track?.track.title ?? ""
        }
        artist={
          he
            ? dailySong.artistHe ?? engine.track?.track.artist ?? ""
            : dailySong.artistEn ?? engine.track?.track.artist ?? ""
        }
        artworkUrl={engine.track?.track.artworkUrl}
        shareData={shareData}
        showShareCard={justFinished && phase === "correct"}
        engineGenre={engine.track?.track.genre}
        displayName={auth.displayName ?? undefined}
        onBack={() => onNavigate?.("home")}
        he={he}
        t={t}
      />
    );
  }

  // Locale-correct title for the LetterBank tiles. Fall back through the
  // bilingual columns: he → title_he, en → title_en, else iTunes raw.
  const targetTitle = he
    ? dailySong.titleHe ?? engine.track?.track.title ?? ""
    : dailySong.titleEn ?? engine.track?.track.title ?? "";
  const targetArtist = he
    ? dailySong.artistHe ?? engine.track?.track.artist ?? ""
    : dailySong.artistEn ?? engine.track?.track.artist ?? "";
  // Page-level score (decoupled from engine.score, which only updates on
  // engine.skip / win). Reflects the live "what would I get if I guessed
  // RIGHT NOW" value, factoring in notes bought + artist hint.
  const notesBought = Math.max(0, engine.stage - 1);
  const displayedScore = Math.max(
    0,
    BASE_SCORE - notesBought * PER_NOTE_PENALTY - hintPenalty
  );

  // ── Playing ──
  return (
    <GameStage
      stage={engine.stage}
      score={displayedScore}
      streak={stats.currentStreak}
      isPlaying={isPlaying}
      ready={engine.ready}
      loading={engine.loading}
      onTogglePlay={() => {
        // First tap (no track loaded yet) starts the daily round.
        // After that, the same button toggles play/pause.
        if (!engine.track && !engine.loading) handleStart();
        else if (isPlaying) engine.pause();
        else engine.play();
      }}
      onRevealHint={handleRevealHint}
      onGuess={handleGuess}
      wrongSignal={wrongSignal}
      dailyNumber={dailyNumber}
      onBack={() => onNavigate?.("home")}
      error={engine.error}
      he={he}
      targetTitle={targetTitle}
      bankLocale={he ? "he" : "en"}
      artistRevealed={artistRevealed}
      artistText={targetArtist}
      onArtistHint={handleArtistHint}
      artistHintCost={ARTIST_HINT_COST}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Game stage — playing phase
// ─────────────────────────────────────────────────────────────────────────

interface GameStageProps {
  stage: number;
  score: number;
  streak: number;
  isPlaying: boolean;
  ready: boolean;
  loading: boolean;
  onTogglePlay: () => void;
  onRevealHint: () => void;
  onGuess: (title: string) => void;
  wrongSignal: number;
  dailyNumber: number;
  onBack: () => void;
  error: string | null;
  he: boolean;
  /** Title to guess (locale-correct), used to seed the LetterBank tiles. */
  targetTitle: string;
  /** Locale for filler-letter alphabet selection. */
  bankLocale: "he" | "en";
  /** Artist hint state */
  artistRevealed: boolean;
  artistText: string;
  onArtistHint: () => void;
  artistHintCost: number;
}

function GameStage({
  stage,
  score,
  streak,
  isPlaying,
  loading,
  onTogglePlay,
  onRevealHint,
  onGuess,
  wrongSignal,
  dailyNumber,
  onBack,
  error,
  he,
  targetTitle,
  bankLocale,
  artistRevealed,
  artistText,
  onArtistHint,
  artistHintCost,
}: GameStageProps) {
  return (
    <div
      dir={he ? "rtl" : "ltr"}
      className="px-5 pt-1 pb-4"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Top: back + title + stage dots */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onBack}
          aria-label={he ? "חזרה" : "Back"}
          className="nig-press nig-tap"
          style={{
            width: 36,
            height: 36,
            borderRadius: 99,
            background: "transparent",
            border: `1px solid ${T.hairline}`,
            display: "grid",
            placeItems: "center",
            padding: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d={he ? "M11 7 L7 3 M11 7 L7 11 M11 7 H2" : "M3 7 L7 3 M3 7 L7 11 M3 7 H12"}
              stroke={T.cream}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 10.5,
              color: T.gold,
              letterSpacing: "0.18em",
              fontWeight: 600,
            }}
          >
            {he ? `אתגר יומי #${dailyNumber}` : `Daily challenge #${dailyNumber}`}
          </div>
          <div
            style={{
              fontFamily: T.serif,
              fontSize: 14,
              fontWeight: 600,
              color: T.cream,
              marginTop: 2,
            }}
          >
            {he ? `תו ${stage} מתוך ${TOTAL_STAGES}` : `Note ${stage} of ${TOTAL_STAGES}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {Array.from({ length: TOTAL_STAGES }).map((_, i) => (
            <div
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: 99,
                background:
                  i < stage - 1
                    ? T.gold
                    : i === stage - 1
                    ? T.goldGlow
                    : T.hairline,
                boxShadow: i === stage - 1 ? `0 0 8px ${T.gold}` : "none",
              }}
            />
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div
        className="flex items-center justify-around"
        style={{
          padding: "8px 0 14px",
          borderBottom: `1px solid ${T.hairline}`,
          marginBottom: 14,
        }}
      >
        <ScoreChip
          icon={<Mark kind="streak" size={14} color={T.goldGlow} />}
          value={streak}
          label={he ? "רצף" : "streak"}
        />
        <ScoreChip
          value={score.toLocaleString()}
          label={he ? "ניקוד" : "score"}
          accent
        />
        <ScoreChip
          value={`${stage}/${TOTAL_STAGES}`}
          label={he ? "תווים" : "notes"}
        />
      </div>

      {/* Big play button + halo */}
      <div className="flex flex-col items-center" style={{ marginTop: 4 }}>
        <BigPlayButton
          isPlaying={isPlaying}
          loading={loading}
          onToggle={onTogglePlay}
        />

        {/* Live waveform */}
        <div style={{ height: 50, width: "100%", marginTop: 10, marginBottom: 6 }}>
          <WaveForm bars={28} height={50} active={isPlaying} color={T.gold} />
        </div>
      </div>

      {/* "Pts at stake" reminder — kept after dropping the visual hint grid
          so the player still sees the score impact at a glance. */}
      <div
        style={{
          fontSize: 10.5,
          color: T.gold,
          textAlign: "center",
          marginTop: 4,
          marginBottom: 10,
        }}
      >
        {he
          ? `${score}+ נק׳ אם נחשתם עכשיו`
          : `+${score} pts if you guess now`}
      </div>

      {/* Artist hint reveal (one-shot) — appears in gold under the play button */}
      {artistRevealed && (
        <div
          className="text-center"
          style={{
            fontFamily: T.serif,
            fontSize: 13,
            fontWeight: 700,
            color: T.gold,
            marginBottom: 10,
          }}
        >
          {he ? "זמר: " : "Artist: "}
          {artistText}
        </div>
      )}

      {/* LetterBank guess */}
      <div style={{ width: "100%", marginBottom: 10 }}>
        <LetterBank
          correctText={targetTitle}
          locale={bankLocale}
          onSubmit={onGuess}
          wrongSignal={wrongSignal}
        />
      </div>

      {/* Two action chips: more notes (cheap, repeatable) + artist hint
          (expensive, one-shot). */}
      <div className="flex" style={{ width: "100%", gap: 8 }}>
        <ActionChip
          icon={<Mark kind="spark" size={14} color={T.gold} strokeWidth={1.6} />}
          label={
            stage >= TOTAL_STAGES
              ? he
                ? "גלו את הניגון"
                : "Reveal niggun"
              : he
              ? "תו נוסף"
              : "More note"
          }
          sub={
            stage >= TOTAL_STAGES
              ? he
                ? "0 נק׳"
                : "0 pts"
              : he
              ? "‎-10 נק׳"
              : "−10 pts"
          }
          onClick={onRevealHint}
        />
        <ActionChip
          icon={<Mark kind="ear" size={14} color={artistRevealed ? T.mist : T.gold} strokeWidth={1.6} />}
          label={he ? "רמז: שם הזמר" : "Hint: artist"}
          sub={
            artistRevealed
              ? he ? "נחשף" : "revealed"
              : `‎-${artistHintCost} ${he ? "נק׳" : "pts"}`
          }
          muted={artistRevealed}
          onClick={artistRevealed ? undefined : onArtistHint}
        />
      </div>

      {error && (
        <p
          className="mt-3 text-center"
          style={{ color: "#ff8a8a", fontSize: 13, fontWeight: 500 }}
        >
          {error}
        </p>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Victory / failure screen — radiating rays + spinning record
// ─────────────────────────────────────────────────────────────────────────

interface VictoryScreenProps {
  won: boolean;
  stage: number;
  score: number;
  guesses: string[];
  currentStreak: number;
  title: string;
  artist: string;
  artworkUrl?: string;
  shareData: ShareData | null;
  showShareCard: boolean;
  engineGenre?: string;
  displayName?: string;
  onBack: () => void;
  he: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function VictoryScreen({
  won,
  stage,
  score,
  guesses,
  currentStreak,
  title,
  artist,
  artworkUrl,
  shareData,
  showShareCard,
  engineGenre,
  displayName,
  onBack,
  he,
  t,
}: VictoryScreenProps) {
  // Map guesses into used/unused 6-segment array (reveal summary)
  const used = Array.from({ length: TOTAL_STAGES }).map((_, i) =>
    i < (won ? stage - 1 : guesses.filter((g) => g === "skip" || g === "wrong").length) ? 1 : 0
  );
  const accent = won ? T.gold : T.cream;

  return (
    <div
      dir={he ? "rtl" : "ltr"}
      style={{
        position: "relative",
        overflow: "hidden",
        color: T.cream,
        fontFamily: T.sans,
        background: won
          ? `radial-gradient(60% 40% at 50% 36%, ${T.gold}30 0%, transparent 70%)`
          : "transparent",
      }}
    >
      {won && <Confetti />}
      {won && <RadiatingRays />}

      <div
        className="nig-stagger"
        style={{
          position: "relative",
          zIndex: 2,
          padding: "20px 20px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Tag + headline */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              fontSize: 10.5,
              color: accent,
              letterSpacing: "0.32em",
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            <span style={{ width: 24, height: 1, background: accent, opacity: 0.5 }} />
            {won
              ? he
                ? "הניגון נחשף"
                : "Niggun revealed"
              : he
              ? "הניגון נשאר"
              : "Niggun stays"}
            <span style={{ width: 24, height: 1, background: accent, opacity: 0.5 }} />
          </div>
          <div
            style={{
              fontFamily: T.serif,
              fontSize: 32,
              fontWeight: 900,
              color: T.cream,
              lineHeight: 1.05,
              marginBottom: 4,
            }}
          >
            {title || (he ? "ניגון" : "Niggun")}
          </div>
          {artist && (
            <div style={{ fontSize: 13, color: T.mist }}>{artist}</div>
          )}
        </div>

        {/* Big spinning record */}
        <div
          style={{
            alignSelf: "center",
            position: "relative",
            marginBottom: 14,
            width: 140,
            height: 140,
          }}
        >
          <div
            style={{
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: artworkUrl
                ? `center / cover no-repeat url(${artworkUrl})`
                : `radial-gradient(circle at 35% 30%, ${T.navySoft} 0%, ${T.navyDeep} 100%)`,
              display: "grid",
              placeItems: "center",
              boxShadow: T.shadowMed,
              animation: "nig-rotate-slow 22s linear infinite",
              position: "relative",
            }}
          >
            <svg
              width="140"
              height="140"
              viewBox="0 0 140 140"
              style={{ position: "absolute", inset: 0 }}
              aria-hidden
            >
              {[64, 55, 46, 38, 30].map((r, i) => (
                <circle
                  key={r}
                  cx="70"
                  cy="70"
                  r={r}
                  stroke={T.gold}
                  strokeOpacity={0.12 + i * 0.05}
                  fill="none"
                />
              ))}
            </svg>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: accent,
                display: "grid",
                placeItems: "center",
                boxShadow: `inset 0 0 0 4px ${T.navyDeep}`,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: T.navyDeep,
                }}
              />
            </div>
          </div>
        </div>

        {/* Stats card */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginBottom: 14,
            padding: "12px 4px",
            borderRadius: T.r2,
            background: `${T.navyMid}cc`,
            border: `1px solid ${won ? T.gold + "30" : T.hairline}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <StatBlock
            value={won ? "+" + score.toLocaleString() : "0"}
            label={he ? "ניקוד" : "score"}
          />
          <StatBlock
            value={won ? `${stage}/${TOTAL_STAGES}` : "—"}
            label={he ? "תו" : "stage"}
            divider
          />
          <StatBlock
            value={String(currentStreak)}
            label={he ? "רצף" : "streak"}
            mark="streak"
          />
        </div>

        {/* Hint reveal summary */}
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              color: T.mist,
              marginBottom: 6,
              letterSpacing: "0.06em",
            }}
          >
            {he ? "התווים שניצלת" : "notes used"}
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {used.map((u, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 99,
                  background: u ? accent : T.hairline,
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: 10.5, color: T.mist, marginTop: 4 }}>
            {won
              ? he
                ? `${stage - 1} תווים · השארת ${TOTAL_STAGES - stage + 1} לבונוס`
                : `${stage - 1} notes used · ${TOTAL_STAGES - stage + 1} saved`
              : he
              ? `כל ה-${TOTAL_STAGES} התווים נחשפו`
              : `all ${TOTAL_STAGES} notes revealed`}
          </div>
        </div>

        {/* Share card (only on first win) */}
        {showShareCard && shareData && (
          <div style={{ marginBottom: 12 }}>
            <VinylShareCard
              data={shareData}
              streak={currentStreak}
              genre={engineGenre}
            />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {shareData && (
            <ShareButton
              data={shareData}
              streak={currentStreak}
              displayName={displayName}
            />
          )}
          <button
            onClick={onBack}
            className="nig-press"
            style={{
              background: "transparent",
              color: T.cream,
              border: `1px solid ${T.hairline}`,
              padding: "12px",
              borderRadius: 99,
              fontFamily: T.sans,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {he ? "חזרה הביתה" : "Back home"}
          </button>
        </div>

        <p
          className="text-center"
          style={{
            marginTop: 14,
            fontSize: 11,
            color: T.mist,
            letterSpacing: "0.04em",
          }}
        >
          {t("daily.nextChallenge")} · <InkCountdownInline />
        </p>
      </div>
    </div>
  );
}

function RadiatingRays() {
  return (
    <svg
      style={{
        position: "absolute",
        top: "32%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
        opacity: 0.55,
        zIndex: 1,
      }}
      width="600"
      height="600"
      viewBox="-300 -300 600 600"
      aria-hidden
    >
      <defs>
        <radialGradient id="rayGlow" cx="0" cy="0" r="280" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={T.gold} stopOpacity="0.4" />
          <stop offset="60%" stopColor={T.gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle r="280" fill="url(#rayGlow)" />
      {Array.from({ length: 24 }).map((_, i) => {
        const a = (i * 15 * Math.PI) / 180;
        const len = 100 + (i % 3) * 60;
        const x2 = Math.cos(a) * len;
        const y2 = Math.sin(a) * len;
        return (
          <line
            key={i}
            x1="0"
            y1="0"
            x2={x2}
            y2={y2}
            stroke={T.gold}
            strokeWidth="0.8"
            strokeOpacity={0.18 + (i % 3) * 0.08}
          />
        );
      })}
      {[60, 110, 160, 220].map((r, i) => (
        <circle
          key={r}
          r={r}
          fill="none"
          stroke={T.gold}
          strokeOpacity={0.25 - i * 0.05}
          strokeWidth="0.8"
          strokeDasharray={i % 2 ? "3 5" : undefined}
        />
      ))}
    </svg>
  );
}

function StatBlock({
  value,
  label,
  divider,
  mark,
}: {
  value: string;
  label: string;
  divider?: boolean;
  mark?: "streak";
}) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "4px 0",
        borderInlineStart: divider ? `1px solid ${T.hairline}` : 0,
        borderInlineEnd: divider ? `1px solid ${T.hairline}` : 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        <div
          style={{
            fontFamily: T.serif,
            fontSize: 22,
            fontWeight: 700,
            color: T.gold,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        {mark && <Mark kind={mark} size={16} color={T.gold} strokeWidth={1.4} />}
      </div>
      <div style={{ fontSize: 10, color: T.mist, marginTop: 4, letterSpacing: "0.08em" }}>
        {label}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Already-played + Start screens
// ─────────────────────────────────────────────────────────────────────────

interface AlreadyPlayedProps {
  existingResult: { stage: number; score: number } | null;
  guesses: string[];
  stats: ReturnType<typeof getStats>;
  shareData: ShareData | null;
  justFinished: boolean;
  engineGenre?: string;
  displayName?: string;
  onBack: () => void;
  he: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function AlreadyPlayedScreen({
  existingResult,
  guesses,
  stats,
  shareData,
  justFinished,
  engineGenre,
  displayName,
  onBack,
  he,
  t,
}: AlreadyPlayedProps) {
  const wasCorrect = existingResult ? existingResult.stage !== 0 : guesses.includes("correct");
  const stageGuessed = existingResult?.stage ?? 0;
  return (
    <div
      dir={he ? "rtl" : "ltr"}
      className="px-5 pt-4 pb-8 flex flex-col items-center"
      style={{ color: T.cream, fontFamily: T.sans, gap: 16 }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.3em",
          color: T.gold,
          textTransform: "uppercase",
        }}
      >
        {t("home.daily.label")}
      </div>
      <h1
        className="font-serif"
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: T.cream,
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        {wasCorrect
          ? t("daily.gotIt", { stage: stageGuessed })
          : t("daily.betterLuck")}
      </h1>

      <div
        className="w-full"
        style={{
          background: T.creamWarm,
          border: `1px solid ${T.gold}40`,
          color: T.ink,
          borderRadius: T.r2,
          padding: 18,
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: T.inkSoft,
          }}
        >
          {t("daily.nextChallenge")}
        </p>
        <div className="mt-1.5">
          <InkCountdown />
        </div>
        <div className="h-px my-4" style={{ background: `${T.gold}40` }} />
        <p style={{ fontSize: 11.5, color: T.inkSoft }}>
          {stats.totalPlayed} {t("common.played")} · {stats.currentStreak}{" "}
          {t("common.streak").toLowerCase()} · {stats.totalScore.toLocaleString()}{" "}
          {t("common.pts")}
        </p>
      </div>

      {shareData && (
        <>
          {justFinished && wasCorrect && (
            <VinylShareCard
              data={shareData}
              streak={stats.currentStreak}
              genre={engineGenre}
            />
          )}
          <ShareButton
            data={shareData}
            streak={stats.currentStreak}
            displayName={displayName}
          />
        </>
      )}

      <button
        onClick={onBack}
        className="nig-press"
        style={{
          background: "transparent",
          color: T.cream,
          border: `1px solid ${T.hairline}`,
          padding: "11px 22px",
          borderRadius: 99,
          fontFamily: T.sans,
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        {he ? "חזרה הביתה" : "Back home"}
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Countdown helpers
// ─────────────────────────────────────────────────────────────────────────

function InkCountdown() {
  const [time, setTime] = useState(getTimeToMidnight());
  useEffect(() => {
    const i = setInterval(() => setTime(getTimeToMidnight()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div
      className="font-serif tabular-nums"
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: T.ink,
        letterSpacing: 1,
      }}
    >
      {String(time.hours).padStart(2, "0")}:{String(time.minutes).padStart(2, "0")}:
      {String(time.seconds).padStart(2, "0")}
    </div>
  );
}

function InkCountdownInline() {
  const [time, setTime] = useState(getTimeToMidnight());
  useEffect(() => {
    const i = setInterval(() => setTime(getTimeToMidnight()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <span style={{ fontFamily: T.mono, fontSize: 11, color: T.cream }}>
      {String(time.hours).padStart(2, "0")}:{String(time.minutes).padStart(2, "0")}:
      {String(time.seconds).padStart(2, "0")}
    </span>
  );
}

function getTimeToMidnight() {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const diff = midnight.getTime() - now.getTime();
  return {
    hours: Math.floor(diff / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}
